// Tenant-scoped picker for the optional ha_instance_id bind on
// SiteCreate. Surfaces a load-error in the same shape
// HaInstanceCreateForm uses (M2 carry from Task 5 review): the
// caller sees `loadFailed` and the picker disables itself + clears
// helper text so the operator can't proceed against stale data.

import { useQuery } from "@tanstack/react-query";

import { haInstances } from "../../api/endpoints/haInstances";
import { qk } from "../../api/queryKeys";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { AutocompleteField } from "../../components/forms/AutocompleteField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import type { HaInstanceOut } from "../../api/types/haInstance";

interface HaInstancePickerProps {
  accountSlug: string;
  tenantSlug: string;
  value: HaInstanceOut | null;
  onSelect: (item: HaInstanceOut | null) => void;
}

export function HaInstancePicker({
  accountSlug,
  tenantSlug,
  value,
  onSelect,
}: HaInstancePickerProps) {
  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.haInstances(accountSlug, tenantSlug),
    queryFn: () => haInstances.list(accountSlug, tenantSlug).then((r) => r.data),
  });

  const items = q.data ?? [];
  const loadFailed = q.error !== null && q.error !== undefined;
  const empty = !q.isPending && !loadFailed && items.length === 0;

  return (
    <>
      {loadFailed && (
        <ErrorBanner
          error={q.error}
          title="Couldn't load HA instances for the picker"
        />
      )}
      <AutocompleteField<HaInstanceOut>
        label="HA instance (optional)"
        items={items}
        value={value}
        onSelect={onSelect}
        getKey={(h) => h.id}
        getLabel={(h) => h.base_url}
        getSecondary={(h) => h.id}
        disabled={loadFailed}
        placeholder={
          loadFailed
            ? "HA instances unavailable — see error above"
            : q.isPending
              ? "Loading HA instances…"
              : empty
                ? "No HA instances on this tenant"
                : "Pick an HA instance to bind (or leave blank)"
        }
        helper={
          loadFailed
            ? undefined
            : empty
              ? "Skip for now — you can bind one from the site detail page after creating the site."
              : "Same-tenant only; backend rejects cross-tenant bindings with 400."
        }
        allowClear
      />
    </>
  );
}
